import { useEffect, useRef, useState } from 'react';
import './App.css';

/* Segments for 2nd wave (use x=48 y=3) */
const SEGS = [
  { x0:-112, y0:47, x1:-82, y1:47, x2:-54, y2:29, x3:-24, y3:29 },
  { x0:-24,  y0:29, x1:6,   y1:29, x2:34,  y2:47, x3:64,  y3:47 },
  { x0:64,   y0:47, x1:94,  y1:47, x2:122, y2:29, x3:152, y3:29 },
  { x0:152,  y0:29, x1:182, y1:29, x2:210, y2:47, x3:240, y3:47 },
];

function bez(t: number, a: number, b: number, c: number, d: number) {
  const s = 1 - t;
  return s*s*s*a + 3*s*s*t*b + 3*s*t*t*c + t*t*t*d;
}

function waveY(x: number) {
  for (const s of SEGS) {
    if (x >= s.x0 - 1 && x <= s.x3 + 1) {
      let lo = 0, hi = 1;
      for (let i = 0; i < 20; i++) {
        const mid = (lo + hi) / 2;
        if (bez(mid, s.x0, s.x1, s.x2, s.x3) < x) lo = mid; else hi = mid;
      }
      const t = (lo + hi) / 2;
      return bez(t, s.y0, s.y1, s.y2, s.y3);
    }
  }
  return 51;
}

function triggerBounce(header: Element) {
  const absumLetters = header.querySelectorAll('.h1 > .logo-letter');
  const corpLetters = header.querySelectorAll('.h2 > .logo-letter');
  const sloganLetters = header.querySelectorAll('.services .letter');
  absumLetters.forEach(s => s.classList.remove('letter-bounce'));
  corpLetters.forEach(s => s.classList.remove('letter-bounce'));
  sloganLetters.forEach(s => s.classList.remove('letter-bounce'));
  absumLetters.forEach((s, i) => {
    setTimeout(() => {
      s.classList.add('letter-bounce');
      s.addEventListener('animationend', () => s.classList.remove('letter-bounce'), { once: true });
    }, i * 80);
  });
  corpLetters.forEach((s, i) => {
    setTimeout(() => {
      s.classList.add('letter-bounce');
      s.addEventListener('animationend', () => s.classList.remove('letter-bounce'), { once: true });
    }, i * 25);
  });
  sloganLetters.forEach((s, i) => {
    const el = s as HTMLElement;
    const origDelay = el.style.animationDelay;
    setTimeout(() => {
      el.style.animationDelay = '0s';
      el.classList.add('letter-bounce');
      el.addEventListener('animationend', () => {
        el.classList.remove('letter-bounce');
        el.style.animationDelay = origDelay;
      }, { once: true });
    }, i * 15);
  });
}

function LegacyApp() {
  const wavesRef = useRef<SVGSVGElement>(null);
  const ballRef = useRef<HTMLDivElement>(null);
  const scoreRef = useRef<HTMLDivElement>(null);
  const waveAreaRef = useRef<HTMLDivElement>(null);
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const gameRef = useRef({ score: 0, active: false });

  useEffect(() => {
    const svg = wavesRef.current;
    const ball = ballRef.current;
    const waveArea = waveAreaRef.current;
    if (!svg || !ball || !waveArea) return;
    let id: number;
    const t0 = performance.now();
    let jumpV = 0;
    let jumpY = 0;
    const gravity = 120;
    let flipAngle = 0;
    let flipping = false;
    let flipCount = 0;
    const flakes: { x: number; y: number; speed: number; el: HTMLDivElement }[] = [];
    let flakeTimer = 0;

    const onDblClick = () => {
      if (!gameRef.current.active) {
        gameRef.current.active = true;
        setGameActive(true);
      }
    };
    document.addEventListener('dblclick', onDblClick);

    const onClick = () => {
      if (!gameRef.current.active) return;
      if (jumpY >= 0) {
        jumpV = -45;
        flipCount = 0;
      } else {
        flipping = true;
        flipCount++;
      }
    };
    document.addEventListener('click', onClick);

    const flashScore = () => {
      const el = scoreRef.current;
      if (el) { el.classList.add('flash'); setTimeout(() => el.classList.remove('flash'), 300); }
    };

    let lastT = performance.now();
    const tick = () => {
      const now = performance.now();
      const dt = (now - lastT) / 1000;
      lastT = now;

      if (jumpY < 0 || jumpV !== 0) {
        jumpV += gravity * dt;
        jumpY += jumpV * dt;
        if (jumpY >= 0) {
          jumpY = 0; jumpV = 0;
          if (flipCount > 0) {
            const points = flipCount * (flipCount + 1) / 2;
            gameRef.current.score += points;
            setScore(gameRef.current.score);
            flashScore();
            const bx = parseFloat(ball.style.left) || 50;
            const by = parseFloat(ball.style.top) || 50;
            const pop = document.createElement('div');
            pop.className = 'point-pop flip-pop';
            pop.textContent = `${flipCount}x flip!`;
            pop.style.left = `${bx}%`;
            pop.style.top = `${by}%`;
            waveArea.appendChild(pop);
            setTimeout(() => pop.remove(), 900);
          }
          flipAngle = 0;
          flipping = false;
          flipCount = 0;
        }
      }

      if (flipping && jumpY < 0) {
        flipAngle += 720 * dt;
      }

      const el = svg.querySelectorAll('use')[1];
      if (el) {
        const ct = getComputedStyle(el).transform;
        let tx = 0;
        if (ct && ct !== 'none') {
          const m = ct.match(/matrix.*?\((.+)\)/);
          if (m) tx = parseFloat(m[1].split(',')[4]) || 0;
        }
        const t = (now - t0) / 1000;
        const xPct = 50 + Math.sin(t * 0.08) * 18 + Math.sin(t * 0.04) * 12 + Math.sin(t * 0.02) * 7;
        const svgX = (xPct / 100) * 150;
        const y = waveY(svgX - tx);
        const topPct = ((y - 24) / 58) * 100 + jumpY;
        ball.style.left = `${xPct}%`;
        ball.style.top = `${topPct}%`;
        ball.style.transform = flipping ? `rotate(${flipAngle}deg)` : 'rotate(0deg)';

        if (gameRef.current.active) {
          flakeTimer += dt;
          if (flakeTimer > 0.6) {
            flakeTimer = 0;
            const clouds = waveArea.querySelectorAll('.game-cloud');
            if (clouds.length > 0) {
              const cloud = clouds[Math.floor(Math.random() * clouds.length)];
              const cr = cloud.getBoundingClientRect();
              const wr = waveArea.getBoundingClientRect();
              const fx = ((cr.left + cr.width / 2 - wr.left) / wr.width) * 100;
              const fy = ((cr.top + cr.height - wr.top) / wr.height) * 100;
              const fel = document.createElement('div');
              fel.className = 'game-flake';
              fel.style.left = `${fx}%`;
              fel.style.top = `${fy}%`;
              waveArea.appendChild(fel);
              flakes.push({ x: fx, y: fy, speed: 7 + Math.random() * 5, el: fel });
            }
          }
          for (let i = flakes.length - 1; i >= 0; i--) {
            const f = flakes[i];
            f.y += f.speed * dt;
            f.el.style.top = `${f.y}%`;
            if (Math.abs(f.x - xPct) < 2.5 && Math.abs(f.y - topPct) < 3) {
              f.el.classList.add('caught');
              const cel = f.el;
              setTimeout(() => cel.remove(), 300);
              flakes.splice(i, 1);
              gameRef.current.score += 1;
              setScore(gameRef.current.score);
              flashScore();
              const pop = document.createElement('div');
              pop.className = 'point-pop';
              pop.textContent = '+1';
              pop.style.left = `${f.x}%`;
              pop.style.top = `${f.y}%`;
              waveArea.appendChild(pop);
              setTimeout(() => pop.remove(), 700);
              continue;
            }
            if (f.y > 105) {
              f.el.remove();
              flakes.splice(i, 1);
            }
          }
        }
      }

      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(id); document.removeEventListener('click', onClick); document.removeEventListener('dblclick', onDblClick); flakes.forEach(f => f.el.remove()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="header">
        {gameActive && score > 0 && <div className="game-score" ref={scoreRef}>{score}</div>}
        <div className="stars">
          <span className="star" style={{top:'8%',left:'12%',animationDelay:'0s'}} />
          <span className="star" style={{top:'15%',left:'45%',animationDelay:'-2s'}} />
          <span className="star" style={{top:'5%',left:'72%',animationDelay:'-4s'}} />
          <span className="star" style={{top:'22%',left:'28%',animationDelay:'-1s'}} />
          <span className="star" style={{top:'10%',left:'88%',animationDelay:'-3s'}} />
          <span className="star" style={{top:'14%',left:'55%',animationDelay:'-5s'}} />
          <span className="star" style={{top:'18%',left:'8%',animationDelay:'-2.5s'}} />
          <span className="star" style={{top:'3%',left:'35%',animationDelay:'-4.5s'}} />
          <span className="star" style={{top:'25%',left:'78%',animationDelay:'-1.5s'}} />
          <span className="star" style={{top:'12%',left:'62%',animationDelay:'-3.5s'}} />
          <span className="star star-bright" style={{top:'6%',left:'20%',animationDelay:'-0.5s'}} />
          <span className="star star-bright" style={{top:'20%',left:'90%',animationDelay:'-2.8s'}} />
          <span className="star star-bright" style={{top:'16%',left:'42%',animationDelay:'-4.2s'}} />
        </div>
        <div className="aurora" />
        <div className="aurora aurora-2" />
        <div className="birds">
          <span className="bird bird-1" />
          <span className="bird bird-2" />
          <span className="bird bird-3" />
          <span className="bird bird-4" />
          <span className="bird bird-5" />
        </div>
        <div className="inner-header flex">
          <div className="h1" onClick={() => {
            const header = document.querySelector('.header');
            if (!header) return;
            triggerBounce(header);
          }}>{'absum'.split('').map((c, i) => (
            <span key={i} className="logo-letter">{c}</span>
          ))}<div className="h2">{'corporation'.split('').map((c, i) => (
            <span key={i} className="logo-letter">{c}</span>
          ))}</div>
          </div>
        </div>
        <div className="services">
          {'where complexity meets clarity'.split('').map((char, i) => (
            <span
              key={i}
              className={char === ' ' ? 'letter space' : 'letter'}
              style={{ animationDelay: `${5.2 + (i / 29) * 2.08}s` }}
            >
              {char === ' ' ? '\u00A0' : char}
            </span>
          ))}
        </div>
      <div className="wave-area" ref={waveAreaRef}>
      {gameActive && <>
        {[1,2,3,4,5,6].map(n => (
          <div key={n} className={`game-cloud game-cloud-${n}`}>
            <div className="cloud-puff puff-a" />
            <div className="cloud-puff puff-b" />
            <div className="cloud-puff puff-c" />
          </div>
        ))}
      </>}
      <svg className="waves waves-back" ref={wavesRef} xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox="0 24 150 58" preserveAspectRatio="none" shapeRendering="auto">
      <defs>
      <path id="gentle-wave" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
      <linearGradient id="wave-grad-1" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="rgba(46,196,182,0.3)" />
        <stop offset="40%" stopColor="rgba(200,240,235,0.2)" />
        <stop offset="100%" stopColor="#fff" />
      </linearGradient>
      <linearGradient id="wave-grad-2" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="rgba(100,220,255,0.25)" />
        <stop offset="50%" stopColor="rgba(220,245,255,0.18)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0.5)" />
      </linearGradient>
      <linearGradient id="wave-grad-3" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="rgba(80,230,200,0.15)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0.35)" />
      </linearGradient>
      </defs>
      <g className="parallax">
      <use xlinkHref="#gentle-wave" x="48" y="0" fill="url(#wave-grad-1)" />
      <use xlinkHref="#gentle-wave" x="48" y="3" fill="url(#wave-grad-2)" />
      </g>
      </svg>
      <div className={`snowman ${gameActive ? 'snowman-active' : ''}`} ref={ballRef}>
        <div className="snowman-inner">
          <div className="snowman-head">
            <div className="snowman-eye snowman-eye-l" />
            <div className="snowman-eye snowman-eye-r" />
          </div>
          <div className="snowman-body">
            <div className="snowman-arm snowman-arm-l" />
            <div className="snowman-arm snowman-arm-r" />
          </div>
          <div className="snowman-board" />
        </div>
      </div>
      <svg className="waves waves-front" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox="0 24 150 58" preserveAspectRatio="none" shapeRendering="auto">
      <defs>
      <path id="gentle-wave-front" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
      <linearGradient id="wave-grad-3b" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="rgba(80,230,200,0.15)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0.35)" />
      </linearGradient>
      <linearGradient id="wave-grad-4" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="rgba(200,240,245,0.6)" />
        <stop offset="30%" stopColor="rgba(255,255,255,.9)" />
        <stop offset="100%" stopColor="#fff" />
      </linearGradient>
      </defs>
      <g className="parallax">
      <use xlinkHref="#gentle-wave-front" x="48" y="5" fill="url(#wave-grad-3b)" />
      <use xlinkHref="#gentle-wave-front" x="48" y="7" fill="url(#wave-grad-4)" />
      </g>
      </svg>
      </div>


      </div>



    <div className="content flex">
      <p>
      <span className='footer-text'>
        <strong>Absum Corporation Oy</strong><br />
        Espoo | Finland | EU
        <br /><br />
        <a href="https://www.linkedin.com/in/absum/" target="_blank" rel="noopener noreferrer" className="linkedin-link">
          <svg className="linkedin-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        </a>
      </span></p>
    </div>
    <div className="bottom-footer">
      <a href="/games/space-adventure/" className="games-link">Space Adventure</a>
    </div>
  </div>
  );
}

function App() {
  return <iframe className="aurora-page" src="/aurora.html" title="Absum Corporation" />;
}

export { LegacyApp };
export default App;
